<template>
  <!-- Loading State -->
  <div v-if="loading" class="loading-container">
    <v-skeleton-loader type="image, article, table" class="mb-6"></v-skeleton-loader>
  </div>

  <div class="need-details-container" v-else>
    <!-- Age Group Warning -->
    <v-alert v-if="need && profileType === 'Volunteer' && isNotAgeGroup()" type="warning" class="mb-4" border="left"
      colored-border elevation="1" dismissible @input="dismissAgeWarning">
      <div class="d-flex align-center">
        <v-icon left>mdi-alert</v-icon>
        This opportunity is not compatible with your age group
      </div>
      <div class="mt-2">
        <div>
          <strong>Required age:</strong>
          <span v-if="need.min_age != 0 && need.max_age != 120 && need.max_age">
            {{ need.min_age }} to {{ need.max_age }} years old
          </span>
          <span v-else-if="need.min_age"> {{ need.min_age }} and up </span>
          <span v-else-if="need.max_age && need.max_age != 120">
            {{ need.max_age }} and younger
          </span>
        </div>
        <div><strong>Your age group:</strong> {{ profile.age_group }}</div>
      </div>
    </v-alert>

    <!-- Incomplete Need Warning -->
    <v-alert v-if="need && !schedules.length && !loadingShifts" type="error" class="mb-4" border="left" colored-border
      elevation="1">
      <div class="d-flex align-center">
        <v-icon left>mdi-alert-circle</v-icon>
        No active shifts.
      </div>
      <div v-if="profileType === 'Agency' && profile._id === need.agency" class="mt-2">
        Please create shift to display this opportunity to volunteers.
      </div>
    </v-alert>

    <!-- Main Need Card -->
    <v-card v-if="need" class="mb-6 need-card" elevation="2">
      <v-card-title class="need-header">
        <div class="header-content">
          <!-- Need Image -->
          <div class="need-image-container">
            <v-img :src="getMainImage()" :width="$vuetify.breakpoint.xs ? '80' : '100'"
              :height="$vuetify.breakpoint.xs ? '80' : '100'" contain class="need-image" @click="openImageDialog(0)"
              style="cursor: pointer;">
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                </v-row>
              </template>
              <template v-slot:error>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-icon large color="grey lighten-2">mdi-image</v-icon>
                </v-row>
              </template>
            </v-img>
          </div>

          <!-- Title and Meta Info -->
          <div class="need-info">
            <div class="title-section">
              <h1 class="need-title">
                {{ need.title }}
                <v-chip small color="primary" text-color="white" class="ml-2" v-if="need.is_urgent">
                  <v-icon small left>mdi-alert</v-icon>
                  Urgent
                </v-chip>
              </h1>

              <!-- Agency Info -->
              <div class="d-flex align-center flex-wrap">
                <div class="agency-link" @click="goToAgencyProfile">
                  <v-avatar size="24" class="mr-2" v-if="need.agency_image">
                    <img :src="need.agency_image" alt="Agency Image" />
                  </v-avatar>
                  <v-icon small class="mr-1" v-else>mdi-account-group</v-icon>
                  {{ need.agency_name }}
                  <v-icon small class="ml-1" v-if="need.agency_verified">mdi-check-decagram</v-icon>
                </div>

                <!-- Status -->
                <div class="ml-2 duration-chip">
                  <v-chip small color="purple lighten-5" text-color="purple darken-2">
                    <v-icon small left>mdi-clock</v-icon>
                    {{ need.duration }}
                  </v-chip>
                </div>
              </div>

              <!-- Address -->
              <!-- <div class="d-flex align-center mt-2 need-location">
                <v-icon small class="mr-1">mdi-map-marker</v-icon>
                {{ need.address ? `${need.address}, ` : "" }}{{ need.city }},
                {{ need.state }} {{ need.zip }}
                <v-btn icon x-small class="ml-1" @click="openMapDirections">
                  <v-icon small>mdi-directions</v-icon>
                </v-btn>
              </div> -->

              <!-- Dates -->
              <div v-if="need.duration !== 'Is Ongoing'" class="d-flex align-center mt-1 need-date">
                <v-icon small class="mr-1">mdi-calendar</v-icon>
                {{ formatDate(need.start_date) }} to
                {{ formatDate(need.end_date) }}
              </div>
            </div>

            <!-- Interest Icons -->
            <div class="d-flex flex-wrap mt-3 need-interests">
              <site-icon v-for="(icon, key) in need.interests" :key="key" :name="icon" type="Interest" class="mr-2 mb-2"
                :size="$vuetify.breakpoint.xs ? '30' : '40'" />
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <div v-if="need.agency === profile._id" class="mr-2">
              <v-btn color="primary" small outlined @click="onResponse">
                <v-icon left small>mdi-account-group</v-icon>
                Volunteer List
              </v-btn>
            </div>

            <div v-else-if="
              profileType === 'Volunteer' || profileType === 'Company'
            " class="mr-2">
              <v-btn color="primary" small outlined @click="goToDiary(profileType)">
                <v-icon left small>mdi-notebook</v-icon>
                View Diary
              </v-btn>
            </div>

            <!-- More Actions Menu -->
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" class="ml-2">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item v-if="need.agency === profile._id" @click="onEditNeed">
                  <v-list-item-title>
                    <v-icon small left color="#3D90D7">mdi-pencil</v-icon>
                    Edit Need
                  </v-list-item-title>
                </v-list-item>
                <v-list-item v-if="need.agency === profile._id" @click="onDeleteNeed">
                  <v-list-item-title>
                    <v-icon small left color="#E83F25">mdi-delete</v-icon>
                    Delete Need
                  </v-list-item-title>
                </v-list-item>
                <v-list-item @click="onShare(need)">
                  <v-list-item-title>
                    <v-icon small left color="#3F7D58">mdi-share-variant</v-icon>
                    Share
                  </v-list-item-title>
                </v-list-item>
                <!-- <v-list-item @click="toggleFavorite">
                  <v-list-item-title>
                    <v-icon small left :color="isFavorite ? 'amber' : 'grey'">mdi-star</v-icon>
                    {{ isFavorite ? 'Remove from favorites' : 'Add to favorites' }}
                  </v-list-item-title>
                </v-list-item> -->
                <v-list-item @click="reportNeed" v-if="profileType !== 'Agency'">
                  <v-list-item-title>
                    <v-icon small left color="error">mdi-flag</v-icon>
                    Report
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </v-card-title>

      <!-- Description and Map -->
      <v-card-text class="need-content">
        <section class="need-section">
          <h2 class="section-title">
            <v-icon color="primary" class="mr-2">mdi-text</v-icon>
            Description
          </h2>
          <div class="section-content text-block" v-linkified>
            {{ need.description }}
          </div>
        </section>

        <v-expansion-panels flat class="mb-4">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <h2 class="section-title">
                <v-icon color="primary" class="mr-2">mdi-map-marker</v-icon>
                Location Details
              </h2>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="section-content">
                <!-- <div class="mb-3">
                  <v-icon small class="mr-1">mdi-office-building</v-icon>
                  <strong>Location:</strong> {{ need.address || 'Not specified' }}
                </div> -->
                <div class="mb-3">
                  <v-icon small class="mr-1">mdi-map-marker</v-icon>
                  <strong>Address:</strong> {{ need.address ? `${need.address}, ` : "" }}{{ need.city }},
                  {{ need.state }} {{ need.zip }}
                </div>
                <div class="mb-3" v-if="need.parking_info">
                  <v-icon small class="mr-1">mdi-parking</v-icon>
                  <strong>Parking:</strong> {{ need.parking_info }}
                </div>
                <div class="mb-3" v-if="need.public_transport">
                  <v-icon small class="mr-1">mdi-bus</v-icon>
                  <strong>Public Transport:</strong> {{ need.public_transport }}
                </div>
                <div class="mb-3" v-if="need.additional_directions">
                  <v-icon small class="mr-1">mdi-sign-direction</v-icon>
                  <strong>Additional Directions:</strong> {{ need.additional_directions }}
                </div>

                <GmapMap v-if="center" :center="center" :zoom="15" map-type-id="terrain" class="location-map" :options="{
                  zoomControl: true,
                  mapTypeControl: true,
                  scaleControl: true,
                  streetViewControl: true,
                  rotateControl: true,
                  fullscreenControl: true,
                  disableDefaultUi: false
                }">
                  <GmapMarker :position="center" :icon="require('@/assets/svg/needs.svg')" @click="openMapDirections" />
                  <GmapInfoWindow :options="infoWindowOptions" :position="center" :opened="infoWindowOpen"
                    @closeclick="infoWindowOpen = false">
                    <div class="info-window-content">
                      <h3>{{ need.title }}</h3>
                      <p>{{ need.address }}</p>
                      <v-btn small color="primary" @click="openMapDirections">
                        Get Directions
                      </v-btn>
                    </div>
                  </GmapInfoWindow>
                </GmapMap>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Image Gallery Section -->
        <section class="need-section" v-if="need.images && need.images.length">
          <h2 class="section-title">
            <v-icon color="primary" class="mr-2">mdi-image-multiple</v-icon>
            Photo Gallery
          </h2>
          <div class="section-content">
            <!-- For multiple images - show slide group -->
            <v-slide-group v-if="need.images.length > 1" v-model="imageSlideGroup" class="pa-4" active-class="success"
              show-arrows center-active>
              <v-slide-item v-for="(img, idx) in need.images" :key="idx" v-slot="{ active }">
                <v-card :color="active ? 'primary' : 'grey lighten-1'" class="ma-4 image-thumbnail" height="120"
                  width="160" @click="openImageDialog(idx)">
                  <v-img :src="getImageUrl(img)" height="100%" contain>
                    <template v-slot:placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                      </v-row>
                    </template>
                    <template v-slot:error>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-icon large color="grey lighten-2">mdi-image</v-icon>
                      </v-row>
                    </template>
                  </v-img>
                  <v-fade-transition>
                    <v-overlay v-if="active" absolute color="primary">
                      <v-btn icon @click.stop="openImageDialog(idx)">
                        <v-icon>mdi-magnify-plus-outline</v-icon>
                      </v-btn>
                    </v-overlay>
                  </v-fade-transition>
                </v-card>
              </v-slide-item>
            </v-slide-group>

            <!-- For single image - show centered display -->
            <div v-else class="single-image-container">
              <v-card class="single-image-card" @click="openImageDialog(0)">
                <v-img :src="getImageUrl(need.images[0])" height="300" contain class="single-image">
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                    </v-row>
                  </template>
                  <template v-slot:error>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-icon large color="grey lighten-2">mdi-image</v-icon>
                    </v-row>
                  </template>
                </v-img>
                <v-card-actions class="justify-center">
                  <v-btn text color="primary" @click="openImageDialog(0)">
                    <v-icon left>mdi-magnify-plus</v-icon>
                    View Full Size
                  </v-btn>
                </v-card-actions>
              </v-card>
            </div>
          </div>
        </section>
      </v-card-text>
    </v-card>

    <!-- Shifts Section -->
    <v-card class="mb-6" elevation="2" v-if="need">
      <v-card-title class="d-flex justify-space-between align-center flex-wrap">
        <h2 class="headline">
          <v-icon left>mdi-calendar-clock</v-icon>
          Available Shifts
        </h2>

        <div class="d-flex align-center mt-2 mt-md-0">
          <v-btn color="primary" small outlined @click="onResponse" class="shift-section-button"
            v-if="need.agency === profile._id">
            <v-icon left small>mdi-account-group</v-icon>
            Volunteer List
          </v-btn>
          <v-btn v-if="need.agency === profile._id" color="primary" small @click="onAddShift" class="ml-2">
            <v-icon left small>mdi-plus</v-icon>
            Add Shift
          </v-btn>
        </div>
      </v-card-title>

      <!-- Active Shift Filters Summary -->
      <v-card-text v-if="hasActiveShiftFilters" class="pb-0">
        <v-card class="filters-summary-chip-group" elevation="1" outlined>
          <v-card-text class="d-flex align-center flex-wrap pa-2">
            <v-icon left color="primary" class="mr-2" small>mdi-filter-variant</v-icon>
            <span class="filter-label font-weight-medium mr-2" style="font-size: 0.85rem;">Active Filters:</span>

            <!-- Date Range Filter -->
            <v-chip v-if="dateRange.length === 2" close @click:close="resetDateRange" color="success" text-color="white"
              small class="mr-2 mb-1">
              <v-icon left small>mdi-calendar</v-icon>
              {{ formatDateRangeDisplay }}
            </v-chip>

            <!-- Time of Day Filter -->
            <v-chip v-if="timeOfDayFilter.length > 0" close @click:close="clearTimeOfDayFilter" color="info"
              text-color="white" small class="mr-2 mb-1">
              <v-icon left small>mdi-clock</v-icon>
              {{ timeOfDayFilter.join(', ') }}
            </v-chip>

            <!-- Availability Filter -->
            <v-chip v-if="availabilityFilter" close @click:close="clearAvailabilityFilter" color="warning"
              text-color="white" small class="mr-2 mb-1">
              <v-icon left small>mdi-account-check</v-icon>
              {{ getAvailabilityLabel(availabilityFilter) }}
            </v-chip>

            <!-- Clear All Button -->
            <v-btn text small color="error" @click="resetAllShiftFilters" class="clear-all-btn ml-2">
              <v-icon left small>mdi-close-circle</v-icon>
              Clear All
            </v-btn>
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-card-text>
        <v-data-table :headers="headers" :items="filteredSchedules" :loading="loadingShifts" :search="shiftSearch"
          :custom-filter="filterShifts" loading-text="Loading shifts... Please wait" no-data-text="No shifts available"
          class="elevation-1 shifts-table" @click:row="goToShift" mobile-breakpoint="0">
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>Available Shifts</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-text-field v-model="shiftSearch" append-icon="mdi-magnify" label="Search shifts" single-line
                hide-details class="search-field"></v-text-field>
              <v-btn icon @click="toggleFilters" class="ml-2">
                <v-icon>mdi-filter</v-icon>
              </v-btn>
            </v-toolbar>

            <!-- Shift Filters Section -->
            <!-- Shift Filters Section -->
            <v-expand-transition>
              <v-card v-show="showFilters" class="filter-card">
                <v-card-text>
                  <!-- Date Range - Full Width Row -->
                  <v-row>
                    <v-col cols="12">
                      <v-menu ref="dateMenu" v-model="dateMenu" :close-on-content-click="false"
                        :return-value.sync="dateRange" transition="scale-transition" offset-y min-width="auto">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field v-model="dateRangeText" label="Select Date Range"
                            prepend-icon="mdi-calendar-range" readonly v-bind="attrs" v-on="on"
                            :error-messages="shiftDateRangeError" clearable @click:clear="resetDateRange" dense outlined
                            hide-details class="date-range-field" placeholder="Select start and end date">
                            <template v-slot:append>
                              <v-icon small color="primary">mdi-chevron-down</v-icon>
                            </template>
                          </v-text-field>
                        </template>
                        <v-date-picker v-model="dateRange" range no-title scrollable :allowed-dates="allowedShiftDates"
                          color="primary">
                          <v-spacer></v-spacer>
                          <v-btn text color="primary" @click="dateMenu = false">
                            Cancel
                          </v-btn>
                          <v-btn text color="primary" @click="saveShiftDateRange">
                            OK
                          </v-btn>
                        </v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>

                  <!-- Other Filters Row -->
                  <v-row>
                    <v-col cols="12" sm="6" md="4">
                      <v-select v-model="timeOfDayFilter" :items="timeOfDayOptions" label="Time of Day" multiple chips
                        clearable dense outlined hide-details prepend-icon="mdi-clock-outline"></v-select>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <v-select v-model="availabilityFilter" :items="availabilityOptions" label="Availability" clearable
                        dense outlined hide-details prepend-icon="mdi-account-check"></v-select>
                    </v-col>
                    <v-col cols="12" sm="6" md="4" class="d-flex align-center justify-end flex-wrap">
                      <v-btn text @click="resetFilters" class="mr-2" color="error">
                        <v-icon left small>mdi-close-circle</v-icon>
                        Reset
                      </v-btn>
                      <v-btn color="primary" @click="applyFilters" class="px-4">
                        <v-icon left small>mdi-check</v-icon>
                        Apply
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-expand-transition>
          </template>

          <!-- Title Column -->
          <template v-slot:item.title="{ item }">
            <div class="shift-title">
              <v-icon small class="mr-2">mdi-calendar-text</v-icon>
              {{ item.title || `Shift ${schedules.indexOf(item) + 1}` }}
              <v-chip x-small color="primary" text-color="white" class="ml-2" v-if="item.is_recurring">
                <v-icon x-small left>mdi-repeat</v-icon>
                Recurring
              </v-chip>
            </div>
          </template>

          <!-- Date Column -->
          <template v-slot:item.begins="{ item }">
            <div class="date-cell">
              <div class="date-display">
                <v-icon small class="mr-1">mdi-calendar</v-icon>
                <span class="date-text">
                  {{ formatFullDate(item.start_date) }}
                </span>
              </div>
              <div class="time-display">
                <v-icon small class="mr-1">mdi-clock</v-icon>
                {{
                  item.hours_description ||
                  formatTimeRange(item.start_date, item.hours)
                }}
              </div>
            </div>
          </template>

          <!-- Duration Column -->
          <template v-slot:item.hours="{ item }">
            <v-chip small color="blue lighten-5" text-color="blue darken-2">
              {{ item.hours }} hours
            </v-chip>
          </template>

          <!-- Capacity Column -->
          <template v-slot:item.capacity="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <div v-on="on" class="capacity-container">
                  <v-progress-circular :value="calculateCapacityPercentage(item)" :size="55" :width="5"
                    :color="getCapacityColor(item)" rotate="-90">
                    <v-icon small v-if="calculateCapacityPercentage(item) >= 100">mdi-alert</v-icon>
                    <span v-else class="font-weight-bold">
                      {{ calculateAvailableSpots(item) }}
                    </span>
                  </v-progress-circular>
                  <div class="capacity-text" v-if="calculateCapacityPercentage(item) < 100">
                    /{{ item.capacity }}
                  </div>
                </div>
              </template>
              <span class="font-weight-bold">
                {{ calculateAvailableSpots(item) }}/{{ item.capacity }}
                available
              </span>
            </v-tooltip>
          </template>

          <!-- Status Column (for volunteers) -->
          <template v-slot:item.status="{ item }">
            <div class="status-indicator" v-if="getResponseStatus(item)">
              <v-icon small :color="getStatusColor(getResponseStatus(item))" class="status-icon mr-1">
                {{ getStatusIcon(getResponseStatus(item)) }}
              </v-icon>
              <span class="status-text" :style="{ color: getStatusColor(getResponseStatus(item)) }">
                {{ getResponseStatus(item) }}
              </span>
            </div>
          </template>

          <!-- Actions Column -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex" v-if="profileType !== 'Admin'">
              <!-- Agency Actions -->
              <template v-if="need.agency === profile._id">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click.stop="onScanQR(item)"
                      class="mr-1">
                      <v-icon small>mdi-qrcode-scan</v-icon>
                    </v-btn>
                  </template>
                  <span>Scan QR</span>
                </v-tooltip>

                <!-- <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click.stop="onEditShift(item)"
                      class="mr-1"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit Shift</span>
                </v-tooltip> -->

                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small color="error" v-bind="attrs" v-on="on" @click.stop="onDeleteShift(item)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete Shift</span>
                </v-tooltip>
              </template>

              <!-- Volunteer/Company Actions -->
              <template v-else>
                <v-btn v-if="checkMyResponse(item)" color="primary" small outlined @click.stop="onRepondShift(item)"
                  class="mr-2 view-btn" :loading="item.loading">
                  <v-icon left>mdi-eye</v-icon>
                  View
                </v-btn>

                <!-- <v-btn
                  v-if="
                    checkConfirmedResponse(item) &&
                      new Date(item.start_date).toDateString() ===
                        new Date().toDateString()
                  "
                  color="success"
                  small
                  outlined
                  @click.stop="startWork(item)"
                  class="mr-2 start-btn"
                  :loading="item.loading"
                >
                  <template
                     v-if="!timerInfo || !timerInfo.res || timerInfo.res.status === 'Completed'"
                  >
                    <v-icon left>mdi mdi-play-circle</v-icon>
                    Begin
                  </template>
<template v-else-if="timerInfo && timerInfo.res && timerInfo.res.status === 'Tracking'">
                    <v-icon left>mdi mdi-stop-circle</v-icon>
                    Stop
                  </template>
</v-btn> -->

                <v-btn v-if="
                  checkConfirmedResponse(item) &&
                  new Date(item.start_date).toDateString() === new Date().toDateString() &&
                  (
                    !timerInfo || !timerInfo.res ||
                    timerInfo.res.shift_id === item._id
                  )
                " color="success" small outlined @click.stop="startWork(item)" class="mr-2 start-btn"
                  :loading="item.loading">
                  <!-- Show BEGIN if not tracking or completed -->
                  <template v-if="
                    !timerInfo ||
                    !timerInfo.res ||
                    (timerInfo.res.status === 'Completed' && timerInfo.res.shift_id === item._id)
                  ">
                    <v-icon left>mdi-play-circle</v-icon>
                    Begin
                  </template>

                  <!-- Show STOP if tracking and same shift -->
                  <template v-else-if="
                    timerInfo.res.status === 'Tracking' &&
                    timerInfo.res.shift_id === item._id
                  ">
                    <v-icon left>mdi-stop-circle</v-icon>
                    Stop
                  </template>
                </v-btn>

                <v-btn v-else-if="
                  item.responses &&
                  item.responses.filter(
                    r => r.shift_id === item._id && r.status !== 'Rejected'
                  ).length === item.capacity
                " color="grey" small disabled class="mr-2" outlined>
                  <v-icon left>mdi-close-circle</v-icon>
                  Full
                </v-btn>
                <v-btn v-else-if="
                  checkSubmittedResponse(item) &&
                  new Date(item.start_date) >= new Date() &&
                  !isNotAgeGroup()
                " color="primary" small outlined @click.stop="onRepondShift(item)" class="mr-2 join-btn">
                  <v-icon left>mdi-handshake</v-icon>
                  Join
                </v-btn>

                <v-btn v-else-if="new Date(item.start_date) < new Date()" color="grey" small disabled class="mr-2">
                  <v-icon left>mdi-calendar-remove</v-icon>
                  Ended
                </v-btn>
              </template>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Additional Details Section -->
    <v-card v-if="need && hasAdditionalDetails()" class="mb-6 details-card" elevation="2">
      <v-card-title class="details-header">
        <h2 class="section-title">
          <v-icon color="primary" class="mr-2">mdi-information</v-icon>
          Additional Details
        </h2>
      </v-card-title>

      <v-card-text>
        <v-list dense class="details-list">
          <!-- Age Requirements -->
          <v-list-item v-if="need.min_age != 0 || (need.max_age && need.max_age != 120)">
            <v-list-item-icon>
              <v-icon color="primary">mdi-account</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Age Requirements</v-list-item-title>
              <v-list-item-subtitle>
                <span v-if="
                  need.min_age != 0 && need.max_age != 120 && need.max_age
                ">
                  {{ need.min_age }} to {{ need.max_age }} years old
                </span>
                <span v-else-if="need.min_age">
                  {{ need.min_age }} and up
                </span>
                <span v-else-if="need.max_age && need.max_age != 120">
                  {{ need.max_age }} and younger
                </span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <!-- Family Friendly -->
          <v-list-item v-if="need.family_friendly && need.family_friendly != 'N/A'">
            <v-list-item-icon>
              <v-icon color="primary">mdi-human-male-female-child</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Family Friendly</v-list-item-title>
              <v-list-item-subtitle>
                {{ need.family_friendly === "Yes" ? "Yes" : "No" }}
                <span v-if="need.family_friendly_details"> - {{ need.family_friendly_details }}</span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <!-- Outdoors -->
          <v-list-item v-if="need.outdoors && need.outdoors != 'N/A'">
            <v-list-item-icon>
              <v-icon color="primary">mdi-weather-sunny</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Outdoors</v-list-item-title>
              <v-list-item-subtitle>
                {{ need.outdoors === "Yes" ? "Yes" : "No" }}
                <span v-if="need.outdoors_details"> - {{ need.outdoors_details }}</span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <!-- Wheelchair Accessible -->
          <v-list-item v-if="
            need.wheelchair_accessible && need.wheelchair_accessible != 'N/A'
          ">
            <v-list-item-icon>
              <v-icon color="primary">mdi-wheelchair-accessibility</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Wheelchair Accessible</v-list-item-title>
              <v-list-item-subtitle>
                {{ need.wheelchair_accessible === "Yes" ? "Yes" : "No" }}
                <span v-if="need.accessibility_details"> - {{ need.accessibility_details }}</span>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <!-- Physical Requirements -->
          <v-list-item v-if="need.physical_requirements">
            <v-list-item-icon>
              <v-icon color="primary">mdi-run</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Physical Requirements</v-list-item-title>
              <v-list-item-subtitle>
                {{ need.physical_requirements }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <!-- Skills Required -->
          <v-list-item v-if="need.skills_required && need.skills_required.length">
            <v-list-item-icon>
              <v-icon color="primary">mdi-certificate</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Skills Required</v-list-item-title>
              <v-list-item-subtitle>
                {{ need.skills_required.join(", ") }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <!-- Additional Attributes -->
          <v-list-item v-if="need.attributes && need.attributes.length != 0">
            <v-list-item-icon>
              <v-icon color="primary">mdi-tag-multiple</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Attributes</v-list-item-title>
              <v-list-item-subtitle>
                {{ need.attributes.join(", ") }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <!-- Additional Notes -->
          <v-list-item v-if="need.additional_notes">
            <v-list-item-icon>
              <v-icon color="primary">mdi-note-text</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Additional Notes</v-list-item-title>
              <v-list-item-subtitle>
                {{ need.additional_notes }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>

    <!-- Contact Information Section -->
    <v-card v-if="need && (need.contact_email || need.contact_phone)" class="mb-6" elevation="2">
      <v-card-title class="details-header">
        <h2 class="section-title">
          <v-icon color="primary" class="mr-2">mdi-account-box</v-icon>
          Contact Information
        </h2>
      </v-card-title>
      <v-card-text>
        <v-list dense>
          <v-list-item v-if="need.contact_name">
            <v-list-item-icon>
              <v-icon color="primary">mdi-account</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Contact Person</v-list-item-title>
              <v-list-item-subtitle>{{ need.contact_name }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="need.contact_email">
            <v-list-item-icon>
              <v-icon color="primary">mdi-email</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Email</v-list-item-title>
              <v-list-item-subtitle>
                <a :href="`mailto:${need.contact_email}`">{{ need.contact_email }}</a>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="need.contact_phone">
            <v-list-item-icon>
              <v-icon color="primary">mdi-phone</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Phone</v-list-item-title>
              <v-list-item-subtitle>
                <a :href="`tel:${need.contact_phone}`">{{ formatPhoneNumber(need.contact_phone) }}</a>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="need.contact_hours">
            <v-list-item-icon>
              <v-icon color="primary">mdi-clock-outline</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Contact Hours</v-list-item-title>
              <v-list-item-subtitle>{{ need.contact_hours }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>

    <!-- Similar Opportunities Section -->
    <v-card v-if="similarNeeds.length > 0" class="mb-6" elevation="2">
      <v-card-title>
        <h2 class="section-title">
          <v-icon color="primary" class="mr-2">mdi-lightbulb-on</v-icon>
          Similar Opportunities
        </h2>
      </v-card-title>
      <v-card-text>
        <v-slide-group class="pa-4" active-class="success" show-arrows>
          <v-slide-item v-for="(similarNeed, idx) in similarNeeds" :key="idx">
            <v-card class="ma-4 similar-need-card" width="300" @click="goToNeed(similarNeed._id)">
              <v-img
                :src="similarNeed.images && similarNeed.images.length ? similarNeed.images[0].url : require('@/assets/svg/needs.svg')"
                height="150px"></v-img>
              <v-card-title class="similar-need-title">
                {{ similarNeed.title }}
                <v-chip x-small color="primary" text-color="white" class="ml-2" v-if="similarNeed.is_urgent">
                  <v-icon x-small left>mdi-alert</v-icon>
                  Urgent
                </v-chip>
              </v-card-title>
              <v-card-text>
                <div class="similar-need-agency">
                  <v-icon small class="mr-1">mdi-account-group</v-icon>
                  {{ similarNeed.agency_name }}
                </div>
                <div class="similar-need-location">
                  <v-icon small class="mr-1">mdi-map-marker</v-icon>
                  {{ similarNeed.city }}, {{ similarNeed.state }}
                </div>
                <div class="similar-need-duration">
                  <v-icon small class="mr-1">mdi-clock</v-icon>
                  {{ similarNeed.duration }}
                </div>
              </v-card-text>
            </v-card>
          </v-slide-item>
        </v-slide-group>
      </v-card-text>
    </v-card>

    <!-- Error State -->
    <v-card v-if="!need && error" class="error-card">
      <v-card-title class="justify-center py-10">
        <v-icon color="error" large class="mr-3">mdi-alert-circle</v-icon>
        {{ error }}
      </v-card-title>
      <v-card-actions class="justify-center pb-8">
        <v-btn color="primary" @click="$router.go(-1)">
          Go Back
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Dialogs -->
    <agency-edit-need :item="need" :dialog="editDialog" :onCloseDialog="onCloseEditDialog" v-if="editDialog && need" />

    <confirm-delete-need-dialog :deleteDialog="deleteDialog" :need="need" :onClose="onCloseConfirmDelete"
      :onDeleted="onDeletedNeed" />

    <confirm-delete-shift-dialog :deleteDialog="deleteShiftDialog" :shift="selectedShift"
      :onClose="onCloseConfirmDelete" :onDeleted="onShiftDeleted" />

    <create-shift v-if="need" :dialog="createShiftDialog" :need_id="need._id" :need="need"
      :onCloseDialog="onCloseCreateShiftDialog" :onCreatedShift="onCreatedShift" />

    <edit-shift :dialog="editShiftDialog" :shift="selectedShift" :onCloseDialog="onCloseEditShiftDialog"
      :onSavedShift="onSavedShift" />

    <response-shift :dialog="responseDialog" :shift="selectedShift" :need="need" :onCloseDialog="onCloseResponseDialog"
      :onResponse="onRespondedShift" :onCancelled="onCancelledShift" />

    <volunteer-time-tracker :dialog="trackerDialog" :onCloseDialog="onCloseTrackerDialog" :need_id="need?._id"
      :shift="selectedShift" v-if="profileType === 'Volunteer' || (profileType === 'Company' && need)" />

    <shift-qr-dialog :dialog="qrDialog" :shift="selectedShift" :onCloseDialog="onCloseQRDialog" />

    <need-share-dialog-vue :dialog="shareDialog" :onClose="onCloseShareDialog" :eventUrlData="this.eventUrlData" />

    <!-- Image Dialog -->
    <v-dialog v-model="imageDialog" max-width="900px">
      <v-card>
        <!-- Show carousel for multiple images -->
        <v-carousel v-if="need && need.images && need.images.length > 1" v-model="imageDialogIndex"
          hide-delimiter-background show-arrows height="550">
          <v-carousel-item v-for="(img, idx) in need.images" :key="idx">
            <v-img :src="getImageUrl(img)" :alt="'Need Image ' + (idx + 1)" contain height="100%" class="dialog-image">
              <template v-slot:placeholder>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                </v-row>
              </template>
              <template v-slot:error>
                <v-row class="fill-height ma-0" align="center" justify="center">
                  <v-icon large color="grey lighten-2">mdi-image-off</v-icon>
                </v-row>
              </template>
            </v-img>
          </v-carousel-item>
        </v-carousel>

        <!-- Show single image -->
        <div v-else-if="need && need.images && need.images.length === 1" class="single-dialog-container">
          <v-img :src="getImageUrl(need.images[0])" alt="Need Image" contain height="550" class="dialog-image">
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
              </v-row>
            </template>
            <template v-slot:error>
              <v-row class="fill-height ma-0" align="center" justify="center">
                <v-icon large color="grey lighten-2">mdi-image-off</v-icon>
              </v-row>
            </template>
          </v-img>
        </div>

        <!-- No images -->
        <div v-else class="no-image-container pa-10 text-center">
          <v-icon size="64" color="grey lighten-2">mdi-image-off</v-icon>
          <p class="mt-4 grey--text">No images available</p>
        </div>

        <v-card-actions class="justify-space-between" v-if="need && need.images && need.images.length > 0">
          <v-btn icon @click="prevImage" v-if="need.images.length > 1">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <span class="image-counter" v-if="need.images.length > 1">
            {{ imageDialogIndex + 1 }} / {{ need.images.length }}
          </span>
          <span v-else class="image-counter">1 / 1</span>
          <v-btn icon @click="nextImage" v-if="need.images.length > 1">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </v-card-actions>

        <v-card-actions class="justify-center">
          <v-btn color="primary" text @click="imageDialog = false">
            Close
          </v-btn>
          <v-btn color="primary" text @click="downloadImage" v-if="need && need.images && need.images.length > 0">
            <v-icon left>mdi-download</v-icon>
            Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Report Dialog -->
    <v-dialog v-model="reportDialog" max-width="600px">
      <v-card>
        <v-card-title class="headline">Report This Opportunity</v-card-title>
        <v-card-text>
          <v-select v-model="reportReason" :items="reportReasons" label="Reason for reporting" required></v-select>
          <v-textarea v-model="reportDetails" label="Additional details"
            hint="Please provide any additional information that might help us understand the issue"></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="reportDialog = false">Cancel</v-btn>
          <v-btn color="primary" text @click="submitReport">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar" :timeout="3000" :color="snackColor">
      {{ snackMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import ConfirmDeleteNeedDialog from "@/components/common/ConfirmDeleteNeedDialog.vue";
import AgencyEditNeed from "./create/AgencyEditNeed.vue";
import CreateShift from "./shift/CreateShift.vue";
import dateFormat from "dateformat";
import ConfirmDeleteShiftDialog from "@/components/common/ConfirmDeleteShiftDialog.vue";
import EditShift from "./shift/EditShift.vue";
import ResponseShift from "../../volunteer/response/ResponseShift.vue";
import VolunteerTimeTracker from "../../volunteer/response/VolunteerTimeTracker.vue";
import SiteIcon from "@/components/common/SiteIcon.vue";
// import NeedPhotoViewer from "../../../components/agency/NeedPhotoViewer.vue";
import ShiftQrDialog from "./shift/ShiftQrDialog.vue";
import NeedShareDialogVue from "./NeedShareDialog.vue";

export default {
  components: {
    ConfirmDeleteNeedDialog,
    AgencyEditNeed,
    CreateShift,
    ConfirmDeleteShiftDialog,
    EditShift,
    ResponseShift,
    VolunteerTimeTracker,
    SiteIcon,
    // NeedPhotoViewer,
    ShiftQrDialog,
    NeedShareDialogVue
  },
  data() {
    return {
      loading: false,
      loadingShifts: false,
      loadingSimilar: false,
      need: null,
      center: null,
      schedules: [],
      similarNeeds: [],
      headers: [
        { text: "Title", value: "title" },
        { text: "Date", value: "begins", sortable: false },
        { text: "Duration", value: "hours" },
        { text: "Open spots", value: "capacity" },
        { text: "Actions", value: "actions", sortable: false }
      ],
      deleteDialog: false,
      qrDialog: false,
      snackbar: false,
      snackMessage: null,
      snackColor: "success",
      editDialog: false,
      selectedShift: null,
      createShiftDialog: false,
      deleteShiftDialog: false,
      editShiftDialog: false,
      responseDialog: false,
      workable: false,
      trackerDialog: false,
      error: null,
      shareDialog: false,
      eventUrlData: {},
      shiftDateRangeError: "",
      shiftDateValidationMessage: "End date must be after start date",
      imageDialog: false,
      imageDialogIndex: 0,
      imageSlideGroup: null,
      infoWindowOpen: true,
      infoWindowOptions: {
        pixelOffset: {
          width: 0,
          height: -35
        }
      },
      isFavorite: false,
      reportDialog: false,
      reportReason: null,
      reportDetails: "",
      reportReasons: [
        "Inappropriate content",
        "Misleading information",
        "Spam or scam",
        "Duplicate opportunity",
        "Other"
      ],
      shiftSearch: "",
      showFilters: false,
      dateMenu: false,
      dateRange: [],
      timeOfDayFilter: [],
      timeOfDayOptions: ["Morning", "Afternoon", "Evening", "Night"],
      availabilityFilter: null,
      availabilityOptions: [
        { text: "Available spots", value: "available" },
        { text: "Fully booked", value: "full" },
        { text: "Past shifts", value: "past" },
        { text: "Today", value: "today" },
        { text: "This week", value: "week" }
      ],
      dismissedAgeWarning: false
    };
  },
  computed: {
    ...mapGetters({
      profile: "auth/getProfile",
      profileType: "auth/getType",
      timezone: "usersetting/getTimeZone"
    }),
    ...mapState("need", {
      timerInfo: "timerInfo"
    }),
    filteredSchedules() {
      let filtered = this.schedules;

      // Apply date range filter
      if (this.dateRange.length === 2) {
        const start = new Date(this.dateRange[0]);
        const end = new Date(this.dateRange[1]);
        end.setHours(23, 59, 59, 999);

        filtered = filtered.filter(item => {
          const itemDate = new Date(item.start_date);
          return itemDate >= start && itemDate <= end;
        });
      }

      // Apply time of day filter
      if (this.timeOfDayFilter.length > 0) {
        filtered = filtered.filter(item => {
          const hours = new Date(item.start_date).getHours();
          return this.timeOfDayFilter.some(time => {
            if (time === "Morning") return hours >= 6 && hours < 12;
            if (time === "Afternoon") return hours >= 12 && hours < 17;
            if (time === "Evening") return hours >= 17 && hours < 21;
            if (time === "Night") return hours >= 21 || hours < 6;
            return false;
          });
        });
      }

      // Apply availability filter
      if (this.availabilityFilter) {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekEnd = new Date(today);
        weekEnd.setDate(weekEnd.getDate() + 7);

        filtered = filtered.filter(item => {
          const itemDate = new Date(item.start_date);
          const availableSpots = this.calculateAvailableSpots(item);

          switch (this.availabilityFilter) {
            case "available":
              return availableSpots > 0 && itemDate >= now;
            case "full":
              return availableSpots <= 0 && itemDate >= now;
            case "past":
              return itemDate < now;
            case "today":
              return itemDate.toDateString() === now.toDateString();
            case "week":
              return itemDate >= today && itemDate <= weekEnd;
            default:
              return true;
          }
        });
      }

      return filtered;
    },
    hasActiveShiftFilters() {
      return this.dateRange.length === 2 ||
        this.timeOfDayFilter.length > 0 ||
        this.availabilityFilter !== null;
    },

    formatDateRangeDisplay() {
      if (this.dateRange.length === 2) {
        const start = new Date(this.dateRange[0]);
        const end = new Date(this.dateRange[1]);
        return `${this.formatDate(start)} - ${this.formatDate(end)}`;
      }
      return "";
    },
    dateRangeText() {
      return this.dateRange.join(' ~ ')
    }
  },
  methods: {
    ...mapActions("need", {
      getNeedDetails: "getNeedDetails",
      getLocation: "getLocation",
      fetchAllShifts: "fetchAllShifts",
      deleteShift: "deleteShift",
      cancelResponse: "cancelResponse",
      fetchSimilarNeeds: "fetchSimilarNeeds",
      toggleFavoriteNeed: "toggleFavoriteNeed",
      reportNeedAction: "reportNeed"
    }),
    getStatusIcon(status) {
      switch (status) {
        case "Confirmed":
          return "mdi-check-circle";
        case "Pending":
          return "mdi-clock-outline";
        case "Rejected":
          return "mdi-close-circle";
        default:
          return "mdi-help-circle";
      }
    },
    clearTimeOfDayFilter() {
      this.timeOfDayFilter = [];
      this.applyFilters();
    },
    clearAvailabilityFilter() {
      this.availabilityFilter = null;
      this.applyFilters();
    },
    getAvailabilityLabel(value) {
      const option = this.availabilityOptions.find(opt => opt.value === value);
      return option ? option.text : value;
    },
    resetAllShiftFilters() {
      this.dateRange = [];
      this.shiftDateRangeError = "";
      this.timeOfDayFilter = [];
      this.availabilityFilter = null;
      this.applyFilters();
    },

    async fetchNeedDetails() {
      this.loading = true;
      try {
        this.need = await this.getNeedDetails({ _id: this.$route.query._id });
        if (this.need) {
          this.getLocationFromAddress();
          this.getAllShifts();
          this.checkFavoriteStatus();
          this.fetchSimilarOpportunities();
        } else {
          this.error = "Need not found";
        }
      } catch (error) {
        console.error("Error loading need:", error);
        this.error = "Failed to load need details";
      } finally {
        this.loading = false;
      }
    },
    async fetchSimilarOpportunities() {
      if (!this.need) return;

      this.loadingSimilar = true;
      try {
        const similar = await this.fetchSimilarNeeds({
          needId: this.need._id,
          limit: 5
        });
        this.similarNeeds = similar.filter(n => n._id !== this.need._id);
      } catch (error) {
        console.error("Error fetching similar needs:", error);
      } finally {
        this.loadingSimilar = false;
      }
    },
    // Get the main image URL or fallback
    getMainImage() {
      if (this.need && this.need.images && this.need.images.length > 0) {
        const img = this.need.images[0];
        return this.getImageUrl(img);
      }
      return require('@/assets/svg/needs.svg');
    },

    // Get image URL from various formats
    getImageUrl(img) {
      if (!img) return require('@/assets/svg/needs.svg');

      // If img is a string (URL)
      if (typeof img === 'string') {
        return img;
      }

      // If img is an object with source, url, or thumbnail
      if (img.source) return img.source;
      if (img.url) return img.url;
      if (img.thumbnail) return img.thumbnail;

      // Fallback
      return require('@/assets/svg/needs.svg');
    },
    async checkFavoriteStatus() {
      if (this.profileType !== 'Volunteer') return;

      try {
        // This would typically call an API to check if this need is favorited by the user
        // For demo purposes, we'll simulate it
        this.isFavorite = Math.random() > 0.5; // Random true/false for demo
      } catch (error) {
        console.error("Error checking favorite status:", error);
      }
    },
    async toggleFavorite() {
      try {
        this.isFavorite = !this.isFavorite;
        await this.toggleFavoriteNeed({
          needId: this.need._id,
          isFavorite: this.isFavorite
        });
        this.snackMessage = this.isFavorite
          ? "Added to your favorites!"
          : "Removed from your favorites";
        this.snackbar = true;
      } catch (error) {
        console.error("Error toggling favorite:", error);
        this.isFavorite = !this.isFavorite; // Revert on error
        this.snackMessage = "Failed to update favorites";
        this.snackColor = "error";
        this.snackbar = true;
      }
    },
    reportNeed() {
      this.reportDialog = true;
    },
    async submitReport() {
      if (!this.reportReason) {
        this.snackMessage = "Please select a reason for reporting";
        this.snackColor = "error";
        this.snackbar = true;
        return;
      }

      try {
        await this.reportNeedAction({
          needId: this.need._id,
          reason: this.reportReason,
          details: this.reportDetails
        });

        this.reportDialog = false;
        this.snackMessage = "Thank you for your report. We'll review it shortly.";
        this.snackColor = "success";
        this.snackbar = true;

        // Reset form
        this.reportReason = null;
        this.reportDetails = "";
      } catch (error) {
        console.error("Error submitting report:", error);
        this.snackMessage = "Failed to submit report. Please try again.";
        this.snackColor = "error";
        this.snackbar = true;
      }
    },
    dismissAgeWarning() {
      this.dismissedAgeWarning = true;
    },
    isAgencyOwner() {
      return this.need && this.profile && this.need.agency === this.profile._id;
    },
    getLocationFromAddress() {
      this.center = { lat: this.need.latitude, lng: this.need.longitude };
    },
    openMapDirections() {
      if (!this.center) return;

      const url = `https://www.google.com/maps/dir/?api=1&destination=${this.center.lat},${this.center.lng}`;
      window.open(url, '_blank');
    },
    getAllShifts() {
      this.loadingShifts = true;
      this.fetchAllShifts({ need_id: this.$route.query._id })
        .then(res => {
          this.loadingShifts = false;
          this.schedules = res;
          this.workable = false;
          for (var i = 0; i < this.schedules.length; i++) {
            for (var j = 0; j < this.schedules[i].responses.length; j++) {
              if (
                this.schedules[i].responses[j].user_id == this.profile._id &&
                this.schedules[i].responses[j].status == "Confirmed"
              )
                this.workable = true;
              break;
            }
            if (this.workable) break;
          }
        })
        .catch(error => {
          console.log(error);
          this.loadingShifts = false;
        });
    },
    formatFullDate(date) {
      if (!date) return "";
      const d = typeof date === "string" ? new Date(date) : date;
      const localDate = this.convertTZ(d, this.timezone);
      return localDate.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    },
    formatTimeRange(startDate, hours) {
      if (!startDate) return "";
      const start = this.convertTZ(startDate, this.timezone);
      const end = new Date(start.getTime() + hours * 60 * 60 * 1000);

      return `${start.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit"
      })} - ${end.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit"
      })}`;
    },

    calculateCapacityPercentage(item) {
      if (item.capacity === 0) return 0;
      return (
        ((item.capacity - this.calculateAvailableSpots(item)) / item.capacity) *
        100
      );
    },
    calculateAvailableSpots(item) {
      return (
        item.capacity -
        (item.responses
          ? item.responses.filter(r => r.status !== "Rejected").length
          : 0)
      );
    },
    getCapacityColor(item) {
      if (item.capacity === 0) return "grey";
      const percentage = this.calculateCapacityPercentage(item);

      if (percentage >= 100) return "red";
      if (percentage >= 75) return "orange";
      if (percentage >= 50) return "blue";
      if (percentage >= 25) return "light-green";
      return "green";
    },
    getStatusColor(status) {
      switch (status) {
        case "Confirmed":
          return "#3F7D58";
        case "Pending":
          return "#F0A04B";
        case "Rejected":
          return "#CB0404";
        default:
          return "#1B56FD";
      }
    },
    hasAdditionalDetails() {
      if (!this.need) return false;
      return (
        this.need.min_age != 0 ||
        this.need.max_age != 120 ||
        (this.need.family_friendly && this.need.family_friendly != "N/A") ||
        (this.need.outdoors && this.need.outdoors != "N/A") ||
        (this.need.wheelchair_accessible &&
          this.need.wheelchair_accessible != "N/A") ||
        (this.need.attributes && this.need.attributes.length != 0) ||
        this.need.physical_requirements ||
        (this.need.skills_required && this.need.skills_required.length) ||
        this.need.additional_notes
      );
    },
    formatDate(date) {
      if (!date) return "";
      // Parse the ISO date string (e.g., "2025-05-31T00:00:00.000+00:00")
      const d = new Date(date);
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      const year = d.getFullYear();
      return `${month}/${day}/${year}`;
    },
    formatTime(date) {
      return dateFormat(this.convertTZ(date, this.timezone), "hh:MM TT");
    },
    formatPhoneNumber(phone) {
      // Format as (XXX) XXX-XXXX
      const cleaned = ('' + phone).replace(/\D/g, '');
      const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
      }
      return phone;
    },
    convertTZ(date, tzString) {
      if (!date) return "";
      if (!tzString) {
        tzString = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      return new Date(
        (typeof date === "string" ? new Date(date) : date).toLocaleString(
          "en-US",
          {
            timeZone: tzString
          }
        )
      );
    },
    isNotAgeGroup() {
      if (this.dismissedAgeWarning) return false;

      if (
        this.need.min_age == 0 &&
        (this.need.max_age || this.need.max_age == 120)
      )
        return false;

      if (!this.profile.age_group) return false;

      let my_min_age = 0;
      let my_max_age = 120;

      switch (this.profile.age_group) {
        case "13-18":
          my_min_age = 13;
          my_max_age = 18;
          break;
        case "19-24":
          my_min_age = 19;
          my_max_age = 24;
          break;
        case "25-34":
          my_min_age = 25;
          my_max_age = 34;
          break;
        case "35-44":
          my_min_age = 35;
          my_max_age = 44;
          break;
        case "45-54":
          my_min_age = 45;
          my_max_age = 54;
          break;
        case "55-64":
          my_min_age = 55;
          my_max_age = 64;
          break;
        case "65 and older":
          my_min_age = 65;
          my_max_age = 120;
          break;
      }

      if (my_max_age < this.need.min_age || my_min_age > this.need.max_age)
        return true;

      return false;
    },
    onEditNeed() {
      this.editDialog = true;
    },
    onCloseEditDialog() {
      this.editDialog = false;
      this.getNeedDetails({ _id: this.$route.query._id }).then(need => {
        this.loading = false;
        this.need = need;
        this.getLocationFromAddress();
      });
    },
    onCloseCreateShiftDialog() {
      this.createShiftDialog = false;
    },
    onAddShift() {
      this.createShiftDialog = true;
    },
    onCreatedShift(shift) {
      this.createShiftDialog = false;
      this.schedules.unshift(shift);
      this.snackMessage = "Shift created successfully!";
      this.snackbar = true;
    },
    onDeleteNeed(need) {
      this.selected = need;
      this.deleteDialog = true;
    },
    onDeletedNeed() {
      this.deleteDialog = false;
      this.$router.back();
    },
    onCloseConfirmDelete() {
      this.deleteDialog = false;
      this.deleteShiftDialog = false;
    },
    onEditShift(shift) {
      this.selectedShift = shift;
      this.editShiftDialog = true;
    },
    onScanQR(item) {
      this.selectedShift = item;
      this.qrDialog = true;
    },
    onDeleteShift(shift) {
      this.selectedShift = shift;
      this.deleteShiftDialog = true;
    },
    onCloseEditShiftDialog() {
      this.editShiftDialog = false;
    },
    onSavedShift(shift) {
      this.editShiftDialog = false;
      this.schedules = this.schedules.map(s => {
        if (s._id != shift._id) {
          return s;
        } else {
          return shift;
        }
      });
      this.snackMessage = "Shift updated successfully!";
      this.snackbar = true;
    },
    onShiftDeleted(shift) {
      this.schedules = this.schedules.filter(s => s._id != shift._id);
      this.deleteShiftDialog = false;
      this.snackMessage = "Shift deleted successfully!";
      this.snackbar = true;
    },
    goToShift(event, { item }) {
      if (this.profile._id == this.need.agency) {
        this.$router.push({
          name: "agency-responses-feeds",
          query: { _id: this.need._id, selected_shift: item._id }
        });
      }
    },
    goToNeed(needId) {
      this.$router.push({
        name: "need-details",
        query: { _id: needId }
      });
    },
    onResponse() {
      this.$router.push({
        name: "agency-responses-feeds",
        query: { _id: this.need._id }
      });
    },
    onRepondShift(shift) {
      this.selectedShift = shift;
      this.responseDialog = true;
    },
    onRespondedShift(response) {
      this.responseDialog = false;
      this.schedules = this.schedules.map(shift => {
        if (shift._id == response.shift_id) {
          shift.responses = shift.responses.filter(r => r._id != response._id);
          shift.responses.push(response);
        }
        return shift;
      });
    },
    onCancelledShift(response) {
      this.responseDialog = false;
      this.schedules = this.schedules.map(shift => {
        if (shift._id == response.shift_id) {
          shift.responses = shift.responses.filter(r => r._id != response._id);
        }
        return shift;
      });
    },
    onCloseResponseDialog() {
      this.responseDialog = false;
    },
    checkMyResponse(shift) {
      for (var i = 0; i < shift.responses.length; i++) {
        if (shift.responses[i].user_id == this.profile._id) return true;
      }
      return false;
    },
    checkConfirmedResponse(shift) {
      for (var i = 0; i < shift.responses.length; i++) {
        if (
          shift.responses[i].user_id == this.profile._id &&
          shift.responses[i].status == "Confirmed"
        )
          return true;
      }
      return false;
    },
    checkSubmittedResponse(shift) {
      for (var i = 0; i < shift.responses.length; i++) {
        if (shift.responses[i].user_id == this.profile._id) return false;
      }
      return true;
    },
    getResponseStatus(shift) {
      for (var i = 0; i < shift.responses.length; i++) {
        if (
          shift.responses[i].user_id == this.profile._id &&
          shift.responses[i].status
        )
          return shift.responses[i].status;
      }
      return "";
    },
    startWork(shift) {
      this.selectedShift = shift;
      this.trackerDialog = true;
    },
    onCloseTrackerDialog() {
      this.trackerDialog = false;
    },
    onCloseQRDialog() {
      this.qrDialog = false;
    },
    onCancelRespond(shift) {
      this.schedules = this.schedules.map(s =>
        s._id == shift._id ? { ...s, loading: true } : s
      );
      this.cancelResponse({ _id: shift._id })
        .then(res => {
          shift.responses = shift.responses.filter(r => r._id != res._id);
          this.schedules = this.schedules.map(s =>
            s._id == shift._id ? shift : s
          );
          this.snackMessage = "Response cancelled successfully";
          this.snackbar = true;
        })
        .catch(error => {
          console.log(error);
          this.snackMessage = "Failed to cancel response";
          this.snackColor = "error";
          this.snackbar = true;
        });
    },
    async onShare(need) {
      this.shareDialog = true;
      this.eventUrlData = need;

      // Generate a short URL if needed
      try {
        // This would typically call an API to generate a short URL
        // For demo, we'll just use the current URL
        this.eventUrlData.shortUrl = window.location.href;
      } catch (error) {
        console.error("Error generating share URL:", error);
      }
    },
    onCloseShareDialog() {
      this.shareDialog = false;
    },
    copyToClipboard(textToCopy) {
      if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(textToCopy);
      } else {
        let textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        return new Promise((res, rej) => {
          document.execCommand("copy") ? res() : rej();
          textArea.remove();
        });
      }
    },
    goToDiary(profile) {
      if (profile === "Volunteer") {
        this.$router.push({
          name: "volunteer-diary",
          query: {
            need_id: this.need._id
          }
        });
      } else if (profile === "Company") {
        this.$router.push({
          name: "company-diary",
          query: {
            need_id: this.need._id
          }
        });
      }
    },
    goToAgencyProfile() {
      this.$router.push({
        name: "agency-profile-details",
        params: { id: this.need.agency }
      });
    },
    openImageDialog(idx) {
      if (this.need && this.need.images && this.need.images.length > idx) {
        this.imageDialogIndex = idx || 0;
        this.imageDialog = true;
      }
    },

    nextImage() {
      if (this.need && this.need.images && this.imageDialogIndex < this.need.images.length - 1) {
        this.imageDialogIndex++;
      }
    },
    prevImage() {
      if (this.imageDialogIndex > 0) {
        this.imageDialogIndex--;
      }
    },
    downloadImage() {
      if (!this.need || !this.need.images || !this.need.images[this.imageDialogIndex]) {
        return;
      }
      const imgUrl = this.need.images[this.imageDialogIndex].url;
      const link = document.createElement('a');
      link.href = imgUrl;
      link.download = `opportunity-image-${this.imageDialogIndex + 1}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    // Method to validate shift date range
    validateShiftDateRange() {
      if (this.dateRange.length === 2) {
        const startDate = new Date(this.dateRange[0]);
        const endDate = new Date(this.dateRange[1]);

        if (endDate < startDate) {
          this.shiftDateRangeError = this.shiftDateValidationMessage;
          return false;
        } else {
          this.shiftDateRangeError = "";
          return true;
        }
      }
      return true;
    },

    // Method to save shift date range with validation
    saveShiftDateRange() {
      if (this.dateRange.length === 2) {
        const startDate = new Date(this.dateRange[0]);
        const endDate = new Date(this.dateRange[1]);

        if (endDate < startDate) {
          this.shiftDateRangeError = this.shiftDateValidationMessage;
          this.showSnackbar("End date cannot be before start date!", "error", "mdi-calendar-alert");
          this.dateRange = [];
          this.dateMenu = false;
          return;
        } else {
          this.shiftDateRangeError = "";
          this.$refs.dateMenu.save(this.dateRange);
          this.dateMenu = false;
          this.applyFilters();
        }
      }
    },

    // Method to reset date range
    resetDateRange() {
      this.dateRange = [];
      this.shiftDateRangeError = "";
      this.applyFilters();
    },

    // Method to restrict allowed dates for shift filter
    allowedShiftDates(date) {
      // If date range has a start date, prevent selecting dates before it
      if (this.dateRange.length === 1) {
        const startDate = new Date(this.dateRange[0]);
        const selectedDate = new Date(date);
        return selectedDate >= startDate;
      }
      return true;
    },

    // applyFilters method with validation
    applyFilters() {
      // Validate date range before applying filters
      if (this.dateRange.length === 2) {
        const startDate = new Date(this.dateRange[0]);
        const endDate = new Date(this.dateRange[1]);

        if (endDate < startDate) {
          this.shiftDateRangeError = this.shiftDateValidationMessage;
          this.showSnackbar("Invalid date range selected!", "error", "mdi-calendar-alert");
          return;
        }
      }

      // If validation passes, apply filters
      this.showFilters = false;
    },

    resetFilters() {
      this.dateRange = [];
      this.shiftDateRangeError = "";
      this.timeOfDayFilter = [];
      this.availabilityFilter = null;
      this.applyFilters();
    },
    filterShifts(value, search, item) {
      // Custom filter for shift search
      search = search.toString().toLowerCase();
      const titleMatch = item.title.toLowerCase().includes(search);
      const dateMatch = this.formatFullDate(item.start_date).toLowerCase().includes(search);
      const timeMatch = this.formatTimeRange(item.start_date, item.hours).toLowerCase().includes(search);
      const descMatch = item.hours_description && item.hours_description.toLowerCase().includes(search);

      return titleMatch || dateMatch || timeMatch || descMatch;
    }
  },
  mounted() {
    if (!this.$route.query._id) {
      this.$router.push({ name: "agency-need-feeds" });
      return;
    }

    // Set headers based on user type
    if (this.profileType == "Volunteer" || this.profileType == "Company") {
      this.headers = [
        { text: "Title", value: "title" },
        { text: "Date", value: "begins", sortable: false },
        { text: "Duration", value: "hours" },
        { text: "Open spots", value: "capacity" },
        { text: "Status", value: "status" },
        { text: "Actions", value: "actions", sortable: false }
      ];
    }

    this.loading = true;
    this.getNeedDetails({ _id: this.$route.query._id })
      .then(need => {
        this.loading = false;
        this.need = need;
        this.getLocationFromAddress();
        this.getAllShifts();
      })
      .catch(error => {
        this.loading = false;
        if (error.response != undefined && error.response.status === 401) {
          this.tokenDialog = true;
        } else {
          console.log(error.response.data.message);
          if (error.response.data.message == "Need not found") {
            this.error = "Deleted or invalid need";
          }
        }
      });
  },
  watch: {
    profileType(newValue) {
      if (newValue == "Volunteer" || newValue == "Company") {
        this.headers = [
          { text: "Title", value: "title" },
          { text: "Date", value: "begins", sortable: false },
          { text: "Duration", value: "hours" },
          { text: "Open spots", value: "capacity" },
          { text: "Status", value: "status" },
          { text: "Actions", value: "actions", sortable: false }
        ];
      }
    },
    dateRange: {
      handler(newVal) {
        if (newVal.length === 2) {
          this.validateShiftDateRange();
        }
      },
      deep: true
    },
    $route() {
      // Reload if route changes (e.g., navigating to a different need)
      this.fetchNeedDetails();
    }
  }
};
</script>

<style scoped>
/* Container Styles */
.need-details-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

/* Card Styles */
.need-card,
.details-card {
  border-radius: 12px;
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.need-card:hover,
.details-card:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1) !important;
}

/* Header Styles */
.need-header {
  padding: 20px 24px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.header-content {
  display: flex;
  flex-direction: column;
  width: 100%;
}

@media (min-width: 960px) {
  .header-content {
    flex-direction: row;
  }
}

.need-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  word-break: break-word;
  display: flex;
  align-items: center;
}

/* Content Styles */
.need-content {
  padding: 24px;
}

.section-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.section-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #495057;
  margin-bottom: 24px;
}

/* Need Image */
.need-image-container {
  flex-shrink: 0;
  margin-bottom: 16px;
}

@media (min-width: 960px) {
  .need-image-container {
    margin-right: 24px;
    margin-bottom: 0;
  }
}

.need-image {
  border-radius: 8px;
  transition: transform 0.3s ease;
}

.need-image:hover {
  transform: scale(1.03);
}

/* Need Meta Info */
.need-info {
  flex-grow: 1;
}

.title-section {
  margin-bottom: 16px;
}

.agency-link {
  color: #1976d2;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  transition: color 0.2s;
}

.agency-link:hover {
  color: #0d47a1;
  text-decoration: underline;
}

.duration-chip {
  margin-left: 8px;
}

.need-location {
  display: flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 4px;
}

.need-date {
  display: flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.9rem;
  margin-bottom: 4px;
}

.need-interests {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  align-items: flex-start;
  margin-top: 16px;
}

@media (min-width: 960px) {
  .action-buttons {
    margin-top: 0;
    margin-left: 24px;
    flex-direction: column;
  }
}

.more-actions {
  border: 1px solid #ddd;
}

/* Map Styles */
.location-map {
  width: 100%;
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
}

.info-window-content {
  padding: 8px;
  max-width: 200px;
}

.info-window-content h3 {
  margin: 0 0 8px 0;
  font-size: 1rem;
  color: #1976d2;
}

.info-window-content p {
  margin: 0 0 8px 0;
  font-size: 0.8rem;
  color: #666;
}

/* Table Styles */
.shifts-table {
  border-radius: 8px;
  overflow: hidden;
}

.shifts-table>>>.v-data-table-header {
  background-color: #f8f9fa;
}

.shift-title {
  font-weight: 500;
  display: flex;
  align-items: center;
}

.shift-date,
.shift-time {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}

.shift-time {
  color: #6c757d;
}

.capacity-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.capacity-text {
  margin-left: 4px;
  font-size: 0.8rem;
  color: #666;
}

/* Status Indicator */
.status-indicator {
  display: inline-flex;
  align-items: center;
}

.status-icon {
  margin-right: 4px;
}

.status-text {
  font-size: 0.8125rem;
  font-weight: 500;
  text-transform: capitalize;
}

/* Details List */
.details-list {
  background-color: transparent !important;
}

.details-list>>>.v-list-item__icon {
  margin-right: 16px;
}

.details-list>>>.v-list-item__title {
  font-weight: 500;
}

.details-list>>>.v-list-item__subtitle {
  white-space: normal;
}

/* Image Gallery */
.image-thumbnail {
  cursor: pointer;
  transition: transform 0.3s ease;
}

.image-thumbnail:hover {
  transform: scale(1.05);
}

.image-counter {
  font-weight: 500;
  color: #666;
}

/* Similar Needs */
.similar-need-card {
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.similar-need-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
}

.similar-need-title {
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  padding-bottom: 8px;
}

.similar-need-agency,
.similar-need-location,
.similar-need-duration {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  margin-bottom: 4px;
  color: #666;
}

/* Filter Card */
.filter-card {
  border-radius: 0 0 8px 8px;
  box-shadow: none;
  border-top: 1px solid #eee;
}

.search-field {
  max-width: 300px;
}

/* Responsive Adjustments */
@media (max-width: 960px) {
  .shifts-table {
    overflow-x: auto;
  }

  .date-cell {
    min-width: 150px;
  }
}

@media (max-width: 600px) {
  .need-details-container {
    padding: 12px;
  }

  .need-title {
    font-size: 1.3rem;
  }

  .section-title {
    font-size: 1.1rem;
  }

  .shifts-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .shifts-header>div {
    margin-top: 12px;
    width: 100%;
    justify-content: flex-end;
  }
}

/* Text Block */
.text-block {
  white-space: pre-line;
}

/* Error Card */
.error-card {
  text-align: center;
  border-radius: 12px;
}

.error-card .v-card__title {
  color: #dc3545;
  font-weight: 500;
}

/* Loading Styles */
.loading-container {
  padding: 20px;
}

/* Button Styles */
.view-btn {
  color: #1976d2 !important;
  border: 1px solid #1976d2;
  border-style: dashed;
  border-radius: 24px;
}

.join-btn {
  color: #1976d2 !important;
  border: 1px solid #1976d2;
  border-style: dashed;
  border-radius: 24px;
}

.start-btn {
  color: #4caf50 !important;
  border: 1px solid #4caf50;
  border-style: dashed;
  border-radius: 24px;
}

/* Error state styles for shift date picker */
.filter-card :deep(.v-input--is-error .v-input__control) {
  border-color: #ff5252 !important;
}

.filter-card :deep(.v-messages__message) {
  color: #ff5252;
  font-weight: 500;
}

.filter-card :deep(.v-picker--date) {
  .v-date-picker-table .v-btn--active {
    background-color: #084468 !important;
  }

  .v-date-picker-table .v-btn--disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

/* Active Shift Filters Summary */
.filters-summary-chip-group {
  border-radius: 8px !important;
  background: rgba(255, 255, 255, 0.95);
  border-left: 4px solid #084468;
  margin-bottom: 8px;
}

.filters-summary-chip-group .v-card__text {
  padding: 8px 12px !important;
}

.filter-label {
  color: #2c3e50;
  font-size: 0.85rem;
}

.clear-all-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.75rem !important;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .filters-summary-chip-group .v-card__text {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 6px;
  }

  .filters-summary-chip-group .v-card__text .v-chip {
    width: 100%;
    justify-content: center;
  }

  .clear-all-btn {
    width: 100%;
    margin-left: 0 !important;
  }
}

/* Single Image Styles */
.single-image-container {
  display: flex;
  justify-content: center;
  padding: 16px;
}

.single-image-card {
  max-width: 600px;
  width: 100%;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-radius: 12px;
  overflow: hidden;
}

.single-image-card:hover {
  transform: scale(1.02);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15) !important;
}

.single-image {
  border-radius: 12px 12px 0 0;
  background-color: #f5f5f5;
}

/* Image Thumbnail Container */
.image-thumbnail {
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border-radius: 8px;
  overflow: hidden;
}

.image-thumbnail:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

/* Dialog Image Styles */
.dialog-image {
  background-color: #f8f9fa;
}

.single-dialog-container {
  padding: 20px;
  background-color: #f8f9fa;
}

.no-image-container {
  background-color: #f8f9fa;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.image-counter {
  font-weight: 500;
  color: #666;
  font-size: 0.9rem;
}
</style>
